<script setup lang="ts">
// Storefront header: three bands on desktop (utility · brand/search/account ·
// categories), one row on phones. Owns the global "/" search shortcut; the
// account dropdown lives in AccountMenu.vue, search suggestions
// live in SearchBox.vue, the "All games" mega menu in CategoryMenu.vue and
// the mobile drawer in MobileNav.vue (toggled through `toggle-nav`). The
// cart button opens the mini cart and is the fly-to-cart target
// (`data-cart-target`); the mobile search icon opens the command palette.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ClipboardList, CreditCard, Heart, Menu, Search, ShoppingCart, Sparkles, Tag } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { useCartStore } from '../stores/cart';
import { useUiStore } from '../stores/ui';
import { fetchSocialLinks, type SocialLink } from '../api/cms';
import { socialIcon } from '../utils/brand-icons';
import AccountMenu from './AccountMenu.vue';
import BrandLogo from './BrandLogo.vue';
import BrandMark from './BrandMark.vue';
import CategoryMenu from './CategoryMenu.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';
import SearchBox from './SearchBox.vue';

const { t } = useI18n();
const auth = useAuthStore();
const cart = useCartStore();
const ui = useUiStore();
const route = useRoute();

defineProps<{ navOpen?: boolean }>();
const emit = defineEmits<{ 'toggle-nav': [] }>();

const searchBox = ref<InstanceType<typeof SearchBox> | null>(null);
const socialLinks = ref<SocialLink[]>([]);

const utilityLinks = computed(() => [
  { to: '/help', label: t('nav.help') },
  { to: '/articles', label: t('nav.articles') },
  { to: '/legal/terms', label: t('nav.terms') },
]);

// "/" focuses search from anywhere that is not already a text field; where
// no inline SearchBox is rendered (phones, the catalog page) it opens the
// command palette instead.
function onKeydown(e: KeyboardEvent) {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
  const target = e.target as HTMLElement | null;
  if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
  if (target?.isContentEditable) return;
  e.preventDefault();
  if (searchBox.value && window.matchMedia('(min-width: 768px)').matches) searchBox.value.focus();
  else ui.openPalette();
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  try {
    socialLinks.value = await fetchSocialLinks();
  } catch {
    /* utility bar simply shows no socials */
  }
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('scroll', onScroll);
});

const showSearch = computed(() => route.name !== 'catalog');

// Utility band folds away once the page is scrolled so the sticky header
// costs 36px less; cart badge pops when something is added.
const scrolled = ref(false);
function onScroll() {
  scrolled.value = window.scrollY > 80;
}
const badgePop = ref(0);
watch(() => cart.count, (now, before) => { if (now > before) badgePop.value++; });

const iconBtn =
  'relative grid size-10 place-items-center rounded-lg text-text-dim transition-colors hover:bg-card hover:text-text';
const catLink =
  'flex h-9 items-center gap-2 rounded-lg px-3 font-display text-[13px] font-semibold text-text-dim transition-colors hover:bg-white/5 hover:text-text [&.router-link-active]:bg-primary/15 [&.router-link-active]:text-text';
</script>

<template>
  <header class="bg-glass sticky top-0 z-40 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]">
    <div class="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-spectrum opacity-80" aria-hidden="true"></div>
    <!-- Band 1 · utility -->
    <div class="hidden overflow-hidden border-b border-white/5 bg-bg/40 transition-[max-height,opacity] duration-300 lg:block" :class="scrolled ? 'max-h-0 opacity-0' : 'max-h-9 opacity-100'">
      <div class="mx-auto flex h-9 w-full max-w-[1280px] items-center justify-between px-6 text-[12px] text-text-dim">
        <nav class="flex items-center gap-5" :aria-label="t('nav.utility')">
          <RouterLink v-for="l in utilityLinks" :key="l.to" :to="l.to" class="transition-colors hover:text-text">{{ l.label }}</RouterLink>
        </nav>
        <div class="flex items-center gap-1">
          <a
            v-for="link in socialLinks"
            :key="link.id"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="link.platform"
            class="grid size-7 place-items-center rounded-md text-text-mute transition-colors hover:bg-card hover:text-text"
          >
            <BrandMark v-if="socialIcon(link.platform)" :icon="socialIcon(link.platform)!" class="size-3.5" />
            <span v-else class="text-[10px] font-semibold uppercase">{{ link.platform.slice(0, 2) }}</span>
          </a>
          <span v-if="socialLinks.length" aria-hidden="true" class="mx-2 h-4 w-px bg-border"></span>
          <LanguageSwitcher compact />
        </div>
      </div>
    </div>

    <!-- Band 2 · brand / search / account -->
    <div class="mx-auto flex h-[68px] w-full max-w-[1280px] items-center gap-3 px-4 sm:px-6">
      <button
        type="button"
        :aria-label="t('nav.openMenu')"
        :aria-expanded="navOpen"
        aria-controls="mobile-nav"
        :class="[iconBtn, 'lg:hidden -ml-2']"
        @click="emit('toggle-nav')"
      >
        <Menu class="size-5" :stroke-width="1.75" />
      </button>

      <RouterLink to="/" class="relative flex shrink-0 items-center" :aria-label="t('footer.home')">
        <span class="pointer-events-none absolute -inset-x-3 -inset-y-2 rounded-full bg-primary/30 blur-xl" aria-hidden="true"></span>
        <BrandLogo size="sm" class="relative" />
      </RouterLink>

      <div v-if="showSearch" class="mx-auto hidden w-full max-w-[560px] md:block">
        <SearchBox ref="searchBox" show-kbd />
      </div>
      <div v-else class="mx-auto hidden flex-1 md:block"></div>

      <div class="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
        <RouterLink v-if="auth.isAuthenticated" to="/favorites" :aria-label="t('nav.favourites')" :class="[iconBtn, 'hidden sm:grid']">
          <Heart class="size-5" :stroke-width="1.75" />
        </RouterLink>

                <button
          v-if="auth.isAuthenticated"
          type="button"
          data-cart-target
          :aria-label="t('nav.cart')"
          aria-haspopup="dialog"
          :aria-expanded="ui.miniCartOpen"
          :class="iconBtn"
          @click="ui.openMiniCart()"
        >
          <ShoppingCart class="size-5" :stroke-width="1.75" />
          <span
            v-if="cart.count > 0"
            :key="badgePop"
            class="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 font-display text-[10px] font-bold leading-none text-primary-ink"
            :class="badgePop > 0 ? 'animate-pop' : ''"
          >{{ cart.count }}</span>
        </button>

        <AccountMenu v-if="auth.isAuthenticated" />

        <div v-else class="flex shrink-0 items-center gap-2">
          <!-- Signed out: locale next to Log in once the utility band (which
               carries it on lg) has folded away; phones use the drawer. -->
          <LanguageSwitcher compact class="hidden sm:block" :class="scrolled ? '' : 'lg:hidden'" />
          <RouterLink to="/login" class="flex h-10 items-center rounded-lg px-2 text-[14px] font-medium text-text-dim transition-colors hover:text-text sm:border sm:border-border sm:px-4 sm:text-text sm:hover:bg-card">{{ t('nav.logIn') }}</RouterLink>
          <RouterLink to="/register" class="flex h-10 items-center rounded-lg bg-brand-gradient px-4 font-display text-[14px] font-semibold text-white shadow-glow transition-[filter] hover:brightness-110">{{ t('nav.signUp') }}</RouterLink>
        </div>
      </div>
    </div>

    <!-- Phone search row: a field-shaped button that opens the palette, so
         search stays visible on the home screen like on every key marketplace. -->
    <div v-if="showSearch" class="px-4 pb-3 md:hidden">
      <button
        type="button"
        aria-haspopup="dialog"
        class="flex h-11 w-full items-center gap-3 rounded-lg border border-white/10 bg-bg/70 px-4 text-left text-[14px] text-text-mute shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-colors hover:border-text-mute"
        @click="ui.openPalette()"
      >
        <Search class="size-[18px] shrink-0" :stroke-width="2" />
        <span class="truncate">{{ t('nav.searchPlaceholder') }}</span>
      </button>
    </div>

    <!-- Band 3 · categories (desktop) -->
    <div class="hidden border-t border-white/5 lg:block">
      <div class="mx-auto flex h-12 w-full max-w-[1280px] items-center justify-between px-6">
        <nav class="flex items-center gap-1" :aria-label="t('nav.browse')">
          <CategoryMenu />
          <RouterLink to="/catalog?q=card" :class="catLink">
            <CreditCard class="size-4" :stroke-width="2" />
            {{ t('nav.giftCards') }}
          </RouterLink>
        </nav>
        <nav class="flex items-center gap-1" :aria-label="t('nav.shortcuts')">
          <RouterLink to="/catalog?sort=price_asc" :class="catLink">
            <Tag class="size-4" :stroke-width="2" />
            {{ t('nav.deals') }}
          </RouterLink>
          <RouterLink to="/catalog?sort=newest" :class="catLink">
            <Sparkles class="size-4" :stroke-width="2" />
            {{ t('nav.newReleases') }}
          </RouterLink>
          <RouterLink v-if="auth.isAuthenticated" to="/orders" :class="catLink">
            <ClipboardList class="size-4" :stroke-width="2" />
            {{ t('nav.orders') }}
          </RouterLink>
        </nav>
      </div>
    </div>

  </header>
</template>
